import type { AceCmsRecord } from '@/lib/aces-cms';
import { fixMojibakeDeep } from '@/lib/fix-mojibake';
import { apiDelete, apiGet, apiPost, apiPut } from './api-client';

/** Payload limpo: AceInfo.status (active|…) ≠ publicationStatus; remove ícone Lucide. */
function toAceApiPayload(input: Partial<AceCmsRecord>): Record<string, unknown> {
  const {
    status: lifecycleStatus,
    publicationStatus,
    createdAt: _c,
    updatedAt: _u,
    visual,
    ...rest
  } = input as Partial<AceCmsRecord> & { visual?: { gradient?: string; glow?: string; icon?: unknown } };

  const payload: Record<string, unknown> = fixMojibakeDeep({ ...rest });

  if (
    publicationStatus === 'draft' ||
    publicationStatus === 'pending_review' ||
    publicationStatus === 'published'
  ) {
    payload.publicationStatus = publicationStatus;
    payload.status = publicationStatus;
  }

  const content: Record<string, unknown> =
    payload.content && typeof payload.content === 'object' && !Array.isArray(payload.content)
      ? { ...(payload.content as Record<string, unknown>) }
      : {};

  // `status` pode chegar com valor de publicação (dados legados da API),
  // então a comparação é feita com o tipo alargado para string.
  const rawLifecycleStatus = lifecycleStatus as string | undefined;
  if (
    rawLifecycleStatus &&
    rawLifecycleStatus !== 'draft' &&
    rawLifecycleStatus !== 'pending_review' &&
    rawLifecycleStatus !== 'published'
  ) {
    content.status = lifecycleStatus;
  }

  if (visual && typeof visual === 'object') {
    content.visual = {
      gradient: visual.gradient,
      glow: visual.glow,
    };
  }

  if (Object.keys(content).length > 0) {
    payload.content = content;
  }

  return payload;
}

function normalizeAce(ace: AceCmsRecord): AceCmsRecord {
  return fixMojibakeDeep(ace);
}

export async function getPublicAces(): Promise<AceCmsRecord[]> {
  const res = await apiGet<{ data: AceCmsRecord[] }>('/aces');
  return (res.data ?? []).map(normalizeAce);
}

export async function getPublicAce(slug: string): Promise<AceCmsRecord | null> {
  try {
    const res = await apiGet<{ data: AceCmsRecord }>(`/aces/${slug}`);
    return res.data ? normalizeAce(res.data) : null;
  } catch {
    return null;
  }
}

export async function getAdminAces(): Promise<AceCmsRecord[]> {
  const res = await apiGet<{ data: AceCmsRecord[] }>('/admin/aces', true);
  return (res.data ?? []).map(normalizeAce);
}

export async function createAce(input: Partial<AceCmsRecord>): Promise<AceCmsRecord> {
  const res = await apiPost<{ data: AceCmsRecord }>('/admin/aces', toAceApiPayload(input), true);
  return res.data;
}

export async function updateAce(slug: string, patch: Partial<AceCmsRecord>): Promise<AceCmsRecord> {
  const res = await apiPut<{ data: AceCmsRecord }>(`/admin/aces/${slug}`, toAceApiPayload(patch), true);
  return res.data;
}

export async function deleteAce(slug: string): Promise<void> {
  await apiDelete(`/admin/aces/${slug}`, true);
}

export async function submitAceForReview(slug: string): Promise<AceCmsRecord> {
  const res = await apiPost<{ data: AceCmsRecord }>(`/admin/aces/${slug}/submit-review`, undefined, true);
  return res.data;
}

export async function approveAce(slug: string): Promise<AceCmsRecord> {
  const res = await apiPost<{ data: AceCmsRecord }>(`/admin/aces/${slug}/approve`, undefined, true);
  return res.data;
}

export async function rejectAce(slug: string): Promise<AceCmsRecord> {
  const res = await apiPost<{ data: AceCmsRecord }>(`/admin/aces/${slug}/reject`, undefined, true);
  return res.data;
}
